import {useMemo, useState} from 'react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Edit} from 'lucide-react'
import {api} from '@/api/client'
import {endpoints} from '@/api/endpoints'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Input} from '@/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {LookupCombobox} from '@/components/shared/LookupCombobox'
import {MutedText} from '@/components/shared/MutedText'
import {PageLayout} from '@/components/shared/PageLayout'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Stack} from '@/components/shared/Stack'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useToast} from '@/hooks/use-toast'
import {usePageTitle} from '@/hooks/use-page-title'
import {permissions} from '@/lib/permissions'
import {CONCEPT_ICON} from '@/lib/concepts'
import type {LookupDescriptor} from '@/types/endpoint-contract'
import {EntityType} from '@/types/entity-link'
import {
    EscalationThreshold,
    ManagerTarget,
    UpdateEscalationThresholdRequest,
} from '@/types/escalation'

const IDENTITY_LOOKUP: LookupDescriptor = {
    endpoint: 'apiInternalIdentitiesLinks',
    searchParam: 'q',
    entityType: 'IDENTITY',
}

interface EditFormState {
    reminderDays: string
    finalReminderDays: string
    escalateToManagerDays: string
    escalateToPlatformOwnerDays: string
    managerTarget: ManagerTarget
    platformOwnerIdentityId: string | null
    platformOwnerName: string | null
    active: boolean
    version: number
}

function toFormState(threshold: EscalationThreshold): EditFormState {
    return {
        reminderDays: String(threshold.reminderDays),
        finalReminderDays: String(threshold.finalReminderDays),
        escalateToManagerDays: String(threshold.escalateToManagerDays),
        escalateToPlatformOwnerDays: String(threshold.escalateToPlatformOwnerDays),
        managerTarget: threshold.managerTarget,
        platformOwnerIdentityId: threshold.platformOwnerIdentityId,
        platformOwnerName: threshold.platformOwnerName,
        active: threshold.active,
        version: threshold.version,
    }
}

function parsePositiveInt(value: string): number | null {
    if (value.trim() === '') return null
    const n = Number(value)
    if (!Number.isInteger(n) || n <= 0) return null
    return n
}

export function EscalationThresholds() {
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    const {toast} = useToast()
    const queryClient = useQueryClient()
    usePageTitle(i18n.escalation.thresholds.pageTitle)

    const canEdit = hasPermission(permissions.escalationThresholds.edit)

    const {data: thresholds = []} = useQuery({
        queryKey: ['escalation-thresholds'],
        queryFn: () => api.get<EscalationThreshold[]>(endpoints.escalationThresholds.list()),
    })

    const [editing, setEditing] = useState<EscalationThreshold | null>(null)
    const [form, setForm] = useState<EditFormState | null>(null)
    const [validationError, setValidationError] = useState<string | null>(null)

    const updateMutation = useMutation({
        mutationFn: async ({id, body}: {id: string, body: UpdateEscalationThresholdRequest}) =>
            api.put<EscalationThreshold>(endpoints.escalationThresholds.update(id), body),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['escalation-thresholds']})
            toast({description: i18n.escalation.thresholds.edit.saveSuccess})
            closeDialog()
        },
    })

    const openEdit = (threshold: EscalationThreshold) => {
        setEditing(threshold)
        setForm(toFormState(threshold))
        setValidationError(null)
    }

    const closeDialog = () => {
        setEditing(null)
        setForm(null)
        setValidationError(null)
    }

    const handleSave = () => {
        if (!editing || !form) return
        const reminderDays = parsePositiveInt(form.reminderDays)
        const finalReminderDays = parsePositiveInt(form.finalReminderDays)
        const managerDays = parsePositiveInt(form.escalateToManagerDays)
        const platformOwnerDays = parsePositiveInt(form.escalateToPlatformOwnerDays)

        if (reminderDays === null || finalReminderDays === null
                || managerDays === null || platformOwnerDays === null) {
            setValidationError(i18n.escalation.thresholds.validation.positive)
            return
        }
        if (!(reminderDays < finalReminderDays && finalReminderDays < managerDays)) {
            setValidationError(i18n.escalation.thresholds.validation.monotonic)
            return
        }
        if (form.active && !form.platformOwnerIdentityId) {
            setValidationError(i18n.escalation.thresholds.validation.platformOwnerRequired)
            return
        }

        updateMutation.mutate({
            id: editing.id,
            body: {
                reminderDays,
                finalReminderDays,
                escalateToManagerDays: managerDays,
                escalateToPlatformOwnerDays: platformOwnerDays,
                managerTarget: form.managerTarget,
                platformOwnerIdentityId: form.platformOwnerIdentityId,
                active: form.active,
                version: form.version,
            },
        })
    }

    const total = thresholds.length

    const countLabel = useMemo(() => {
        return i18n.escalation.thresholds.count({count: total})
    }, [i18n, total])

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.approvalPolicy}
            title={i18n.escalation.thresholds.title}
            count={total}
            description={countLabel}
            data-testid="escalation-thresholds-list"
        >
            <Table>
                <TableHeader>
                    {/* read-only */}
                    <TableRow>
                        <TableHead data-testid="th-request-type" data-help-key="requestType">{i18n.escalation.thresholds.columns.requestType}</TableHead>
                        <TableHead data-testid="th-risk-level" data-help-key="riskLevel">{i18n.escalation.thresholds.columns.riskLevel}</TableHead>
                        <TableHead data-testid="th-reminder-days" data-help-key="reminderDays">{i18n.escalation.thresholds.columns.reminderDays}</TableHead>
                        <TableHead data-testid="th-final-reminder-days" data-help-key="finalReminderDays">{i18n.escalation.thresholds.columns.finalReminderDays}</TableHead>
                        <TableHead data-testid="th-manager-days" data-help-key="managerDays">{i18n.escalation.thresholds.columns.managerDays}</TableHead>
                        <TableHead data-testid="th-platform-owner-days" data-help-key="platformOwnerDays">{i18n.escalation.thresholds.columns.platformOwnerDays}</TableHead>
                        <TableHead data-testid="th-manager-target" data-help-key="managerTarget">{i18n.escalation.thresholds.columns.managerTarget}</TableHead>
                        <TableHead data-testid="th-active" data-help-key="active">{i18n.escalation.thresholds.columns.active}</TableHead>
                        <TableHead data-testid="th-active-snapshots" data-help-key="activeSnapshots">{i18n.escalation.thresholds.columns.activeSnapshots}</TableHead>
                        {canEdit && <TableHead data-testid="th-actions" data-help-key="actions">{i18n.escalation.thresholds.columns.actions}</TableHead>}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {thresholds.map((threshold) => (
                        <TableRow key={threshold.id} data-testid={`threshold-row-${threshold.id}`} data-readonly>
                            <TableCell>
                                <Badge variant={BadgeVariant.OUTLINE}>{threshold.requestType}</Badge>
                            </TableCell>
                            <TableCell>
                                {threshold.riskLevelCode ?? (
                                    <MutedText>{i18n.escalation.thresholds.defaultRiskLabel}</MutedText>
                                )}
                            </TableCell>
                            <TableCell>{threshold.reminderDays}</TableCell>
                            <TableCell>{threshold.finalReminderDays}</TableCell>
                            <TableCell>{threshold.escalateToManagerDays}</TableCell>
                            <TableCell>{threshold.escalateToPlatformOwnerDays}</TableCell>
                            <TableCell>
                                {i18n.escalation.thresholds.managerTarget[threshold.managerTarget]}
                            </TableCell>
                            <TableCell>
                                <Badge variant={threshold.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                    {threshold.active ? i18n.common.active : i18n.common.inactive}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant={BadgeVariant.COUNT}>{threshold.referencedByActiveSnapshots}</Badge>
                            </TableCell>
                            {canEdit && (
                                <TableCell>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openEdit(threshold)}
                                        data-testid={`btn-edit-threshold-${threshold.id}`}
                                        aria-label={i18n.escalation.thresholds.actions.edit}
                                    >
                                        <Icon icon={Edit} size="sm"/>
                                    </Button>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={!!editing} onOpenChange={(open) => { if (!open) closeDialog() }}>
                <DialogContent data-testid="edit-threshold-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.escalation.thresholds.edit.title}</DialogTitle>
                        <DialogDescription>{i18n.escalation.thresholds.edit.description}</DialogDescription>
                    </DialogHeader>
                    {form && editing && (
                        <Stack gap="md">
                            <FormGrid columns={2}>
                                <FormField label={i18n.escalation.thresholds.edit.reminderDays} htmlFor="reminder-days">
                                    <Input
                                        id="reminder-days"
                                        type="number"
                                        min={1}
                                        value={form.reminderDays}
                                        onChange={(e) => setForm({...form, reminderDays: e.target.value})}
                                        data-testid="input-reminder-days"
                                    />
                                </FormField>
                                <FormField label={i18n.escalation.thresholds.edit.finalReminderDays} htmlFor="final-reminder-days">
                                    <Input
                                        id="final-reminder-days"
                                        type="number"
                                        min={1}
                                        value={form.finalReminderDays}
                                        onChange={(e) => setForm({...form, finalReminderDays: e.target.value})}
                                        data-testid="input-final-reminder-days"
                                    />
                                </FormField>
                                <FormField label={i18n.escalation.thresholds.edit.managerDays} htmlFor="manager-days">
                                    <Input
                                        id="manager-days"
                                        type="number"
                                        min={1}
                                        value={form.escalateToManagerDays}
                                        onChange={(e) => setForm({...form, escalateToManagerDays: e.target.value})}
                                        data-testid="input-manager-days"
                                    />
                                </FormField>
                                <FormField label={i18n.escalation.thresholds.edit.platformOwnerDays} htmlFor="platform-owner-days">
                                    <Input
                                        id="platform-owner-days"
                                        type="number"
                                        min={1}
                                        value={form.escalateToPlatformOwnerDays}
                                        onChange={(e) => setForm({...form, escalateToPlatformOwnerDays: e.target.value})}
                                        data-testid="input-platform-owner-days"
                                    />
                                </FormField>
                                <FormField label={i18n.escalation.thresholds.edit.managerTarget} htmlFor="manager-target" fullWidth>
                                    <Select
                                        value={form.managerTarget}
                                        onValueChange={(v) => setForm({...form, managerTarget: v as ManagerTarget})}
                                    >
                                        <SelectTrigger id="manager-target" data-testid="select-manager-target">
                                            <SelectValue/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ManagerTarget.APPROVER_MANAGER}>
                                                {i18n.escalation.thresholds.managerTarget.APPROVER_MANAGER}
                                            </SelectItem>
                                            <SelectItem value={ManagerTarget.OBJECT_OWNER_MANAGER}>
                                                {i18n.escalation.thresholds.managerTarget.OBJECT_OWNER_MANAGER}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <FormField label={i18n.escalation.thresholds.edit.platformOwner} htmlFor="platform-owner-recipient" fullWidth>
                                    <LookupCombobox
                                        mode="single"
                                        value={form.platformOwnerIdentityId
                                            ? {
                                                id: form.platformOwnerIdentityId,
                                                displayName: form.platformOwnerName ?? form.platformOwnerIdentityId,
                                                entityType: EntityType.IDENTITY,
                                                subType: null,
                                                code: null,
                                            }
                                            : null}
                                        onChange={(next) => setForm({
                                            ...form,
                                            platformOwnerIdentityId: next ? next.id : null,
                                            platformOwnerName: next ? next.displayName : null,
                                        })}
                                        lookup={IDENTITY_LOOKUP}
                                        displayName={String(i18n.escalation.thresholds.edit.platformOwner)}
                                        testIdPrefix="platform-owner-recipient"
                                    />
                                </FormField>
                            </FormGrid>
                            <InlineRow gap="sm" align="center">
                                <Checkbox
                                    id="threshold-active"
                                    checked={form.active}
                                    onCheckedChange={(c) => setForm({...form, active: c === true})}
                                    data-testid="checkbox-active"
                                />
                                <label htmlFor="threshold-active">{i18n.escalation.thresholds.edit.active}</label>
                            </InlineRow>
                            {validationError && (
                                <MutedText data-testid="validation-error">{validationError}</MutedText>
                            )}
                            <SaveCancelFooter
                                onCancel={closeDialog}
                                onSave={handleSave}
                                saveType="button"
                                saveLabel={i18n.escalation.thresholds.edit.save}
                                cancelLabel={i18n.escalation.thresholds.edit.cancel}
                                saving={updateMutation.isPending}
                                testIdPrefix="threshold-edit"
                            />
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </PageLayout>
    )
}
